import { ProjectHeadVO, ProjectHistoryEntryVO, UserInfoVO } from '../model/vo/project.vo';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { ProjectHistoryActionDef } from '../components/project/constants/project-history-action-def';
import { ProjectStatesDef } from '../components/project/constants/project-states-def';
import { StringUtils } from '@shared/util/string-utils';
import { Injectable } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class ProjectHistoryService {
  constructor(
    private localeResourceManager: LocaleResourceManager,
  ) { }

  public getDisplayUsername(userInfo: UserInfoVO): string {
    if (!userInfo) {
      return this.localeResourceManager.getValueByKey('global.userprofile.unknown_user');
    } else if (userInfo.isDeleted) {
      return this.localeResourceManager.getValueByKey('global.userprofile.deleted_user');
    } else {
      return userInfo.username;
    }
  }

  public getFinalizationStateText(project: ProjectHeadVO): string | undefined {
    if (!project) {
      return undefined;
    }
    if (project.isLocked) {
      // in case project is locked, we show locked status tooltip, no matter what latest action is
      return this.localeResourceManager.getValueByKey('projectStateDescription.locked_project');
    }
    return this.getFinalizationStateToolTipByFinalizationStateAndDocumentVersion(
      project.finalizationState ?? undefined,
      project.documentVersion,
      !project.latestProjectHistoryEntry ? ProjectHistoryActionDef.UNDEFINED : project.latestProjectHistoryEntry.action,
      !project.latestProjectHistoryEntry ? undefined : this.getDisplayUsername(project.latestProjectHistoryEntry.modifiedBy),
      !project.latestProjectHistoryEntry ? undefined : project.latestProjectHistoryEntry.receiverEmail
    );
  }

  public getFinalizationStateTooltip(historyEntry: ProjectHistoryEntryVO): string {
    return this.getFinalizationStateToolTipByFinalizationStateAndDocumentVersion(
      historyEntry.finalizationState,
      historyEntry.documentVersion,
      historyEntry.action,
      this.getDisplayUsername(historyEntry.modifiedBy),
      historyEntry.receiverEmail
    );
  }

  private getFinalizationStateToolTipByFinalizationStateAndDocumentVersion(
    finalizationState: number | undefined,
    documentVersion: number,
    action: number,
    modifiedBy: string | undefined,
    receiverEmail: string | undefined
  ): string {
    let toolTip: string = '';
    if (action === ProjectHistoryActionDef.UNDEFINED || action === ProjectHistoryActionDef.UNLOCKED_PROJECT) {
      switch (finalizationState) {
        case ProjectStatesDef.FINALIZATION_STATE_NEW:
          toolTip = this.localeResourceManager.getValueByKey('projectStateDescription.edited_project');
          break;
        case ProjectStatesDef.FINALIZATION_STATE_REQUESTED:
          toolTip = this.localeResourceManager.getValueByKey('projectStateDescription.requested_project');
          break;
        case ProjectStatesDef.FINALIZATION_STATE_ORDERED:
          toolTip = this.localeResourceManager.getValueByKey('projectStateDescription.ordered_project');
          break;
        case ProjectStatesDef.FINALIZATION_STATE_PREPARATION:
          toolTip = this.localeResourceManager.getValueByKey('projectStateDescription.edited_project');
          break;
        case ProjectStatesDef.FINALIZATION_STATE_PDM_EXPORT_DONE:
          toolTip = this.localeResourceManager.getValueByKey('projectStateDescription.finalized_project');
          break;
        case ProjectStatesDef.FINALIZATION_STATE_LEGACY_PROJECT_EXPORT_DONE:
          toolTip = this.localeResourceManager.getValueByKey('projectStateDescription.finalized_legacy_project');
          break;
        case ProjectStatesDef.FINALIZATION_STATE_FINALIZED_WITHOUT_PDM:
          toolTip = this.localeResourceManager.getValueByKey('projectStateDescription.finalized_project_without_pdm');
          break;
        default:
          break;
      }
    } else {
      switch (action) {
        case ProjectHistoryActionDef.CREATED_PROJECT:
          toolTip = this.localeResourceManager.getValueByKey('projectStateDescription.created_project');
          break;
        case ProjectHistoryActionDef.REQUESTED_PROJECT:
          toolTip = this.localeResourceManager.getValueByKey('projectStateDescription.requested_project');
          break;
        case ProjectHistoryActionDef.ORDERED_PROJECT:
          toolTip = this.localeResourceManager.getValueByKey('projectStateDescription.ordered_project');
          break;
        case ProjectHistoryActionDef.EDITED_PROJECT:
          toolTip = this.localeResourceManager.getValueByKey('projectStateDescription.edited_project');
          break;
        case ProjectHistoryActionDef.FINALIZED_PROJECT:
          toolTip = this.localeResourceManager.getValueByKey('projectStateDescription.finalized_project');
          break;
        case ProjectHistoryActionDef.FINALIZED_LEGACY_PROJECT:
          toolTip = this.localeResourceManager.getValueByKey('projectStateDescription.finalized_legacy_project');
          break;
        case ProjectHistoryActionDef.FINALIZED_PROJECT_WITHOUT_PDM:
          toolTip = this.localeResourceManager.getValueByKey('projectStateDescription.finalized_project_without_pdm');
          break;
        case ProjectHistoryActionDef.CHANGED_FINALIZATION_STATE:
          toolTip = this.localeResourceManager.getValueByKey('projectStateDescription.changed_finalization_state');
          break;
        case ProjectHistoryActionDef.CREATED_NEW_PROJECT_VERSION:
          toolTip = this.localeResourceManager.getValueByKey('projectStateDescription.edited_project');
          break;
        case ProjectHistoryActionDef.LOCKED_PROJECT:
          toolTip = this.localeResourceManager.getValueByKey('projectStateDescription.locked_project');
          break;
        default:
          break;
      }
    }

    if (!StringUtils.isEmpty(toolTip)) {
      if (StringUtils.isNotEmpty(receiverEmail) && StringUtils.isNotEmpty(modifiedBy)) {
        toolTip += '\n(' + modifiedBy + ' -> ' + receiverEmail + ')';
      } else if (StringUtils.isNotEmpty(modifiedBy)) {
        toolTip += '\n(' + modifiedBy + ')';
      }
    }

    return toolTip;
  }
}
